import { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, X } from "lucide-react";
import { useCart } from "@/context/CartContext";

const HIDDEN_PREFIXES = ["/checkout", "/order-confirmation", "/admin", "/checkin", "/ticket"];

export default function CartReminderBanner() {
  const { items, count, subtotal, setOpen } = useCart();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(false);

  // Show only at the start of a fresh browser session when a persisted cart exists
  const [eligible] = useState(() => {
    try {
      if (sessionStorage.getItem("abh_session_active")) return false;
      sessionStorage.setItem("abh_session_active", "1");
      const cart = JSON.parse(localStorage.getItem("abh_cart") || "[]");
      return Array.isArray(cart) && cart.length > 0;
    } catch {
      return false;
    }
  });

  const hidden =
    !eligible || dismissed || items.length === 0 || HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));

  const review = () => {
    setDismissed(true);
    setOpen(true);
  };

  return (
    <AnimatePresence>
      {!hidden && (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[80] flex justify-center px-3 pb-3 sm:pb-6">
          <motion.div
            data-testid="cart-reminder-banner"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1, transition: { type: "spring", damping: 26, stiffness: 300, delay: 1 } }}
            exit={{ y: 80, opacity: 0, transition: { duration: 0.25, ease: "easeIn" } }}
            className="pointer-events-auto flex w-full max-w-xl items-center gap-3 rounded-2xl border border-gold/25 bg-ink-card/95 px-4 py-3 shadow-2xl shadow-black/60 backdrop-blur-xl"
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gold/30 bg-gold/10">
              <ShoppingBag className="h-4 w-4 text-gold" />
            </span>
            <p data-testid="cart-reminder-text" className="min-w-0 flex-1 text-xs leading-snug text-foreground sm:text-sm">
              Welcome back! You still have{" "}
              <span className="font-semibold text-gold">
                {count} item{count === 1 ? "" : "s"}
              </span>{" "}
              waiting in your cart{subtotal > 0 ? ` — $${subtotal.toFixed(2)}` : ""}.
            </p>
            <button
              data-testid="cart-reminder-review-button"
              onClick={review}
              className="shrink-0 rounded-full bg-gold px-4 py-2 text-xs font-semibold text-ink transition-colors hover:bg-gold-hover"
            >
              Review Cart
            </button>
            <button
              data-testid="cart-reminder-dismiss-button"
              aria-label="Dismiss cart reminder"
              onClick={() => setDismissed(true)}
              className="shrink-0 text-muted-foreground transition-colors hover:text-gold"
            >
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
